import { createContext, useEffect, useState } from 'react';
import useLocalStorageState from 'use-local-storage-state';
import { useGetAppVersionCalculationReviewBuildNumber } from '../api/app-version/app-version';
import { useGetEnterprise } from '../api/enterprise/enterprise';
import { useGetClaimsForCurrentUser } from '../api/user/user';
import { CalculationExecutionStatusCode } from '../model';
import { OPENED_REPORTS_NAME } from '../utils/BrowserStorageKeys.constants';
import { CommandType } from '../utils/EventGrid.constants';
export interface IUserSettingsProviderProps {
    children?: React.ReactNode;
    userSettings?: IUserSettingsData;
}
export interface IUserSettingsData {
    mostRecentlyOpenedTabs?: ITaxReturnTab[];
}

export interface IUserSettings {
    mostRecentlyOpenedTabs?: ITaxReturnTab[];
    updateReturnTabs: (tab: ITaxReturnTab, isAdd: boolean) => void;
    clearReturnTabs: () => void;
    removeReturn: (key: number) => void;
    environmentName: string;
    buildVersion: string;
    /**
     * Session-only (not persisted) record of which returns we've already learned default to the Forms
     * tab (no report, or a report with no rows) rather than Report. Lets "recently opened" links jump
     * straight to /forms instead of landing on the bare report URL and immediately redirecting.
     */
    defaultsToFormsByReturnKey: Record<number, boolean>;
    setReturnDefaultsToForms: (key: number, defaultsToForms: boolean) => void;
}

export interface ITaxReturnTab {
    returnKey: number;
    returnName?: string;
    statusCode?: CalculationExecutionStatusCode;
    commandType?: CommandType;
}

const userSettings: IUserSettings = {
    mostRecentlyOpenedTabs: [],
    updateReturnTabs: (_tab?: ITaxReturnTab, _isAdd?: boolean) => {},
    clearReturnTabs: () => {},
    removeReturn: (_key: number) => {},
    environmentName: '',
    buildVersion: '',
    defaultsToFormsByReturnKey: {},
    setReturnDefaultsToForms: (_key: number, _defaultsToForms: boolean) => {},
};

export const updateTaxReturnTabs = (
    currentTabs: ITaxReturnTab[],
    tab?: ITaxReturnTab,
    isAdd?: boolean,
    clearTabs = false
): ITaxReturnTab[] => {
    const maxTabs = 40;
    let copyTabs = [...currentTabs];
    const index = copyTabs.findIndex((item) => item.returnKey === tab?.returnKey);
    if (index >= 0) {
        const oldTabObject = copyTabs.splice(index, 1);
        tab = { ...oldTabObject[0], ...tab };
    }
    if (isAdd) {
        if (tab) copyTabs.unshift(tab);
    }
    if (copyTabs.length > maxTabs) {
        copyTabs.pop();
    }
    if (clearTabs) {
        copyTabs = [];
    }
    return copyTabs;
};

export const UserSettingsContext = createContext(userSettings);

export function UserSettingsProvider(props: IUserSettingsProviderProps) {
    const userClaims = useGetClaimsForCurrentUser();
    const { data: buildData } = useGetAppVersionCalculationReviewBuildNumber();
    const environmentId = userClaims?.data?.environmentId || '';
    const userId = userClaims?.data?.userId || '';
    const { data: enterpriseId, isSuccess } = useGetEnterprise();
    const openedReportStorageName = `${environmentId}-${userId}-${enterpriseId?.enterpriseId ?? ''}_${OPENED_REPORTS_NAME}`;
    const [openedReportsStorageValue, setOpenedReportsStorageValue, { removeItem: clearOpenedReportsStorageValues }] = useLocalStorageState(
        openedReportStorageName,
        {
            defaultValue: [] as ITaxReturnTab[],
            storageSync: isSuccess,
        }
    );

    const [environmentName, setEnvironmentName] = useState('');
    const [buildVersion, setBuildVersion] = useState('');
    const [defaultsToFormsByReturnKey, setDefaultsToFormsByReturnKey] = useState<Record<number, boolean>>({});

    useEffect(() => {
        setEnvironmentName(userClaims?.data?.environmentName || '');
        setBuildVersion(buildData || '');
    }, [userClaims?.data?.environmentName, buildData]);

    const updateReturnTabs = (tab: ITaxReturnTab, isAdd: boolean, clearAll = false): void => {
        const latestTabs = updateTaxReturnTabs(openedReportsStorageValue || [], tab, isAdd, clearAll);
        setOpenedReportsStorageValue(latestTabs);
    };
    const clearReturnTabs = (): void => {
        clearOpenedReportsStorageValues();
    };

    const removeReturn = (key: number) => {
        const filteredEntities = openedReportsStorageValue.filter((item) => item.returnKey !== key);
        setOpenedReportsStorageValue(filteredEntities);
    };

    const setReturnDefaultsToForms = (key: number, defaultsToForms: boolean): void => {
        setDefaultsToFormsByReturnKey((current) => {
            if (current[key] === defaultsToForms) {
                return current;
            }
            return { ...current, [key]: defaultsToForms };
        });
    };

    return (
        <UserSettingsContext.Provider
            value={{
                mostRecentlyOpenedTabs: openedReportsStorageValue,
                updateReturnTabs,
                clearReturnTabs,
                removeReturn,
                environmentName,
                buildVersion,
                defaultsToFormsByReturnKey,
                setReturnDefaultsToForms,
            }}
        >
            {props.children}
        </UserSettingsContext.Provider>
    );
}

export default UserSettingsProvider;
